import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  clearSession,
  fetchMe,
  getStoredUser,
  hasToken,
  loginWithPassword,
  logout as apiLogout,
  subscribeSession,
  type AuthUser,
} from '@/lib/api'
import {
  canEdit as rbacCanEdit,
  canView as rbacCanView,
  getPerfilMeta,
  homePath,
  isAdmin,
  isColab,
  isRH,
  normalizePerfil,
  type Perfil,
} from '@/lib/rbac'

type AuthContextValue = {
  user: AuthUser | null
  loading: boolean
  isAuthenticated: boolean
  perfil: Perfil
  perfilLabel: string
  perfilCor: string
  homePath: string
  canView: (page: string) => boolean
  canEdit: (page: string) => boolean
  isAdmin: boolean
  isRH: boolean
  isColab: boolean
  login: (email: string, password: string) => Promise<AuthUser | null>
  logout: () => Promise<void>
  refreshMe: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => getStoredUser<AuthUser>())
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!hasToken()) {
        if (!cancelled) {
          setUser(null)
          setLoading(false)
        }
        return
      }
      try {
        const me = await fetchMe()
        if (!cancelled) setUser(me)
      } catch {
        clearSession()
        if (!cancelled) setUser(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    return subscribeSession((snap) => {
      if (!snap.hasToken) {
        setUser(null)
        return
      }
      const stored = getStoredUser<AuthUser>()
      if (stored) setUser(stored)
    })
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const u = await loginWithPassword(email, password)
    setUser(u)
    return u
  }, [])

  const logout = useCallback(async () => {
    await apiLogout()
    setUser(null)
  }, [])

  const refreshMe = useCallback(async () => {
    if (!hasToken()) {
      setUser(null)
      return
    }
    const me = await fetchMe()
    setUser(me)
  }, [])

  const perfil = normalizePerfil(user?.perfil)
  const meta = getPerfilMeta(perfil)

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      isAuthenticated: Boolean(user && hasToken()),
      perfil,
      perfilLabel: meta.label,
      perfilCor: meta.cor,
      homePath: homePath(perfil),
      canView: (page: string) => rbacCanView(perfil, page),
      canEdit: (page: string) => rbacCanEdit(perfil, page),
      isAdmin: isAdmin(perfil),
      isRH: isRH(perfil),
      isColab: isColab(perfil),
      login,
      logout,
      refreshMe,
    }),
    [user, loading, perfil, meta.label, meta.cor, login, logout, refreshMe],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth deve ser usado dentro de AuthProvider')
  return ctx
}
